/**
 * Puts every send that came back into the message box (mission stream
 * contract phases 4 and 6, agion/Agion#2327): an answer typed there that
 * failed or was refused, and a message sent while the agent works that did
 * not arrive. Several can come back in one tick (a run end fails every
 * message still waiting); the message box then gets their texts in the order
 * they were handed back, which is send order, each separated by a blank line
 * and followed by any draft typed since, and the files of every one. Each
 * return applies once.
 *
 * What the message box holds from then on survives a reload of the same
 * conversation (failedSendStore): the text as the person has it now, and the
 * staged files as the host's references. It is kept until the box is empty
 * again, which is how a send leaves it and how the person dismisses it; a
 * send that comes back again is kept again.
 */

import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react'
import { returnsInOrder, type ReturnedAnswer } from './useAnswerDelivery'
import { forgetFailedSend, loadFailedSend, saveFailedSend } from '../utils/failedSendStore'

/** The message box after sends with `texts` came back into it over `draft`. */
export function withReturnedTexts(draft: string, texts: string[]): string {
  const text = texts.filter((t) => t !== '').join('\n\n')
  return draft.trim() && draft !== text ? `${text}\n\n${draft}` : text
}

/** Whose message box this is, and what it holds now. */
interface ComposerContents {
  /** Unknown until the user is: nothing is kept for a reload until then. */
  userId: string | undefined
  missionId: string
  text: string
  /** The staged files on the host (useChatFileUpload getAttachments). */
  attachments: ReturnedAnswer['attachments']
}

interface UseComposerReturnsOptions {
  /** The chat's return channel (useAnswerDelivery returnedAnswer). */
  returnedAnswer: ReturnedAnswer | null
  setInputValue: Dispatch<SetStateAction<string>>
  /** Stages files already on the host again; adds to the staged ones. */
  restoreFiles: (attachments: ReturnedAnswer['attachments']) => void
  composer: ComposerContents
}

/** The conversation and user a message box belongs to: the ones it was mounted for. */
interface Owner {
  missionId: string
  /** Set once the user is known, which can be after mount. */
  userId: string | undefined
}

/** Whether `owner` is the conversation and user named now; fixes the user once known. */
function ownedBy(owner: Owner, userId: string | undefined, missionId: string): userId is string {
  owner.userId ??= userId
  return !!userId && owner.missionId === missionId && owner.userId === userId
}

export function useComposerReturns({ returnedAnswer, setInputValue, restoreFiles, composer }: UseComposerReturnsOptions): void {
  const appliedRef = useRef(new Set<string>())
  const { userId, missionId, text, attachments } = composer
  // The message box holds one conversation's text and files: the chat mounts
  // once per conversation (MissionChatWithArtifacts), and a box whose
  // conversation or user changes under it keeps and restores nothing, so it
  // never writes one conversation's text under another's key.
  const ownerRef = useRef<Owner>({ missionId, userId })
  // Whether the message box holds a send that came back, and whether it has
  // shown it yet: the box is empty for a render after a return or a reload,
  // and only an empty box after that forgets the send.
  const heldRef = useRef<{ shown: boolean } | null>(null)

  useEffect(() => {
    const fresh = returnsInOrder(returnedAnswer).filter((r) => r.origin === 'composer' && !appliedRef.current.has(r.id))
    if (fresh.length === 0) return
    for (const r of fresh) appliedRef.current.add(r.id)
    setInputValue((draft) => withReturnedTexts(draft, fresh.map((r) => r.text)))
    for (const r of fresh) restoreFiles(r.attachments)
    heldRef.current ??= { shown: false }
  }, [returnedAnswer, setInputValue, restoreFiles])

  // A reload: the send kept for this conversation comes back as a return does.
  useEffect(() => {
    heldRef.current = null
    if (!ownedBy(ownerRef.current, userId, missionId)) return
    const kept = loadFailedSend(userId, missionId)
    if (!kept) return
    heldRef.current = { shown: false }
    setInputValue((draft) => withReturnedTexts(draft, [kept.text]))
    restoreFiles(kept.attachments)
  }, [userId, missionId, setInputValue, restoreFiles])

  useEffect(() => {
    const held = heldRef.current
    if (!held || !ownedBy(ownerRef.current, userId, missionId)) return
    if (text.trim() === '' && attachments.length === 0) {
      if (!held.shown) return
      forgetFailedSend(userId, missionId)
      heldRef.current = null
      return
    }
    held.shown = true
    saveFailedSend(userId, missionId, { text, attachments })
  }, [userId, missionId, text, attachments])
}
